import { GridState, PlacedPiece, getPieceCells, Piece, getShapeForPiece, DEFAULT_CONFIG } from '@game/engine';
import styles from './Grid.module.css';

const CELL_SIZE = 40;

interface GridProps {
  grid: GridState;
  pieces: PlacedPiece[];
  previewPiece?: Piece & { gridX: number; gridY: number };
  onCellClick: (x: number, y: number) => void;
  onPieceClick: (pieceId: string) => void;
  onExpandMap?: (dimension: 'width' | 'height') => void;
  onShrinkMap?: (dimension: 'width' | 'height') => void;
  shrinkHover?: 'width' | 'height' | null;
  onShrinkHoverChange?: (val: 'width' | 'height' | null) => void;
  isPlacementMode?: boolean;
  onPieceHover?: (pieceId: string) => void;
  onPieceLeave?: () => void;
  onPieceContextMenu?: (pieceId: string) => void;
  onExitPlacementMode?: () => void;
  tax?: number;
  rent?: number;
  prestigeLevel?: number;
  selectedPieceId?: string | null;
}


export function Grid({
  grid,
  pieces,
  previewPiece,
  onCellClick,
  onPieceClick,
  onExpandMap,
  onShrinkMap,
  shrinkHover,
  onShrinkHoverChange,
  isPlacementMode,
  onPieceHover,
  onPieceLeave,
  onPieceContextMenu,
  onExitPlacementMode,
  tax = 0,
  rent = 0,
  prestigeLevel = 1,
  selectedPieceId = null,
}: GridProps) {
  const getMapSizeCfg = (w: number, h: number) =>
    DEFAULT_CONFIG.mapSizeConfigs[`${w}x${h}x${prestigeLevel}`] ?? { tax: 0, rent: 0, expansionCost: 0 };

  const isPreviewValid = previewPiece
    ? !getShapeForPiece(previewPiece).cells.some(
        (cell) => grid.cells[previewPiece.gridY + cell.y]?.[previewPiece.gridX + cell.x]
      ) &&
      getShapeForPiece(previewPiece).cells.every(
        (cell) =>
          previewPiece.gridX + cell.x >= 0 &&
          previewPiece.gridX + cell.x < grid.width &&
          previewPiece.gridY + cell.y >= 0 &&
          previewPiece.gridY + cell.y < grid.height
      )
    : false;

  const expandWidth = grid.width < 8 ? 2 * CELL_SIZE : 0;
  const expandHeight = grid.height < 8 ? 2 * CELL_SIZE : 0;

  return (
    <div className={styles.gridContainer} style={{
      width: grid.width * CELL_SIZE + 4 + expandWidth,
      height: grid.height * CELL_SIZE + 4 + expandHeight,
    }}>
      <div
        className={styles.grid}
        style={{
          width: grid.width * CELL_SIZE + 4,
          height: grid.height * CELL_SIZE + 4,
        }}
        onContextMenu={(e) => {
          if (isPlacementMode) {
            e.preventDefault();
            onExitPlacementMode?.();
          }
        }}
      >
        {/* Grid cells */}
        {Array.from({ length: grid.height }).map((_, y) =>
          Array.from({ length: grid.width }).map((_, x) => (
            <div
              key={`cell-${x}-${y}`}
              className={styles.gridCell}
              onClick={() => onCellClick(x, y)}
              style={{ left: x * CELL_SIZE, top: y * CELL_SIZE }}
              title={`${x}, ${y}`}
            />
          ))
        )}

        {/* Placed pieces */}
        {pieces.map((piece) => {
          const cells = getPieceCells(piece);
          const minX = Math.min(...cells.map(c => c.x));
          const minY = Math.min(...cells.map(c => c.y));
          const isSelected = piece.id === selectedPieceId;

          return (
            <div
              key={piece.id}
              className={styles.pieceWrapper}
              style={{ left: minX * CELL_SIZE, top: minY * CELL_SIZE }}
              onClick={(e) => { e.stopPropagation(); onPieceClick(piece.id); }}
              onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onPieceContextMenu?.(piece.id); }}
              onMouseEnter={() => onPieceHover?.(piece.id)}
              onMouseLeave={() => onPieceLeave?.()}
            >
              {cells.map((cell, idx) => (
                <div
                  key={`${piece.id}-${idx}`}
                  className={`${styles.piece} ${styles[piece.state]} ${styles[`piece${piece.type}`]}${isSelected ? ` ${styles.pieceSelected}` : ''}`}
                  style={{
                    left: (cell.x - minX) * CELL_SIZE,
                    top: (cell.y - minY) * CELL_SIZE,
                  }}
                >
                  {piece.level > 1 && <span className={styles.levelBadge}>{piece.level}</span>}
                </div>
              ))}
            </div>
          );
        })}

        {/* Preview piece */}
        {previewPiece && (
          <div className={styles.previewWrapper}>
            {getShapeForPiece(previewPiece).cells.map((cell, idx) => (
              <div
                key={`preview-${idx}`}
                className={`${styles.previewPiece} ${styles[`piece${previewPiece.type}`]} ${isPreviewValid ? styles.valid : ''}`}
                style={{
                  left: (previewPiece.gridX + cell.x) * CELL_SIZE,
                  top: (previewPiece.gridY + cell.y) * CELL_SIZE,
                }}
              />
            ))}
          </div>
        )}

        {/* Right expansion area */}
        {grid.width < 8 && (() => {
          const newCfg = getMapSizeCfg(grid.width + 2, grid.height);
          const taxDelta = newCfg.tax - tax;
          const rentDelta = newCfg.rent - rent;
          return (
            <div
              className={styles.expansionArea}
              style={{
                right: -2 * CELL_SIZE - 2,
                top: -2,
                width: 2 * CELL_SIZE,
                height: grid.height * CELL_SIZE + 4,
              }}
              onClick={() => onExpandMap?.('width')}
            >
              <span>${newCfg.expansionCost}</span>
              {taxDelta !== 0 && <span className={taxDelta > 0 ? styles.deltaPositive : styles.deltaNegative}>{taxDelta > 0 ? '+' : ''}{taxDelta}🏛</span>}
              {rentDelta !== 0 && <span className={rentDelta > 0 ? styles.deltaPositive : styles.deltaNegative}>{rentDelta > 0 ? '+' : ''}{rentDelta}🏠</span>}
            </div>
          );
        })()}

        {/* Bottom expansion area */}
        {grid.height < 8 && (() => {
          const newCfg = getMapSizeCfg(grid.width, grid.height + 2);
          const taxDelta = newCfg.tax - tax;
          const rentDelta = newCfg.rent - rent;
          return (
            <div
              className={styles.expansionArea}
              style={{
                left: -2,
                bottom: -2 * CELL_SIZE - 2,
                width: grid.width * CELL_SIZE + 4,
                height: 2 * CELL_SIZE,
              }}
              onClick={() => onExpandMap?.('height')}
            >
              <span>${newCfg.expansionCost}</span>
              {taxDelta !== 0 && <span className={taxDelta > 0 ? styles.deltaPositive : styles.deltaNegative}>{taxDelta > 0 ? '+' : ''}{taxDelta}🏛</span>}
              {rentDelta !== 0 && <span className={rentDelta > 0 ? styles.deltaPositive : styles.deltaNegative}>{rentDelta > 0 ? '+' : ''}{rentDelta}🏠</span>}
            </div>
          );
        })()}

        {/* Shrink columns */}
        {grid.width > 4 && (
          <>
            {shrinkHover === 'width' && (
              <div
                className={styles.shrinkHighlight}
                style={{
                  top: 0,
                  left: (grid.width - 2) * CELL_SIZE,
                  width: 2 * CELL_SIZE,
                  height: grid.height * CELL_SIZE,
                }}
              />
            )}
            <button
              title="Sell last 2 columns (80% refund)"
              className={`${styles.shrinkButton} ${styles.shrinkButtonWidth}`}
              style={{
                top: -18,
                left: (grid.width - 2) * CELL_SIZE,
                width: 2 * CELL_SIZE + 2,
                height: 16,
              }}
              onMouseEnter={() => onShrinkHoverChange?.('width')}
              onMouseLeave={() => onShrinkHoverChange?.(null)}
              onClick={(e) => { e.stopPropagation(); onShrinkMap?.('width'); onShrinkHoverChange?.(null); }}
            >
              ✕
            </button>
          </>
        )}

        {/* Shrink rows */}
        {grid.height > 4 && (
          <>
            {shrinkHover === 'height' && (
              <div
                className={styles.shrinkHighlight}
                style={{
                  left: 0,
                  top: (grid.height - 2) * CELL_SIZE,
                  width: grid.width * CELL_SIZE,
                  height: 2 * CELL_SIZE,
                }}
              />
            )}
            <button
              title="Sell last 2 rows (80% refund)"
              className={`${styles.shrinkButton} ${styles.shrinkButtonHeight}`}
              style={{
                left: -20,
                top: (grid.height - 2) * CELL_SIZE,
                height: 2 * CELL_SIZE + 2,
                width: 18,
              }}
              onMouseEnter={() => onShrinkHoverChange?.('height')}
              onMouseLeave={() => onShrinkHoverChange?.(null)}
              onClick={(e) => { e.stopPropagation(); onShrinkMap?.('height'); onShrinkHoverChange?.(null); }}
            >
              ✕
            </button>
          </>
        )}
      </div>
    </div>
  );
}
